<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import Comment from './Comment.vue';
import ColorPicker from './ColorPicker.vue';
import { storeToRefs } from 'pinia';
import { onClickOutside, useMediaQuery } from '@vueuse/core';
import { getColorHEX } from '../../../colors';
import { showNotification } from '../../../notifications/notification_actions';
import { useDraftStore } from '@/dashboard/store/draftStore';
import { useDraftImageStore } from '@/dashboard/store/draftImageStore';

const props = defineProps<{
  point: any;
  zoom: number;
  loading: boolean;
}>();

const draftStore = useDraftStore();
const draftImageStore = useDraftImageStore();
const isSmartphone = useMediaQuery('(max-width: 1093px)');

const opacity = ref(1);
const sendingComment = ref(false);
const colorPickerButton = ref<HTMLElement | null>(null);
const messengerRef = ref<HTMLElement | null>(null);
const textareaRef = ref<HTMLTextAreaElement | null>(null);
const commentsRef = ref<HTMLElement | null>(null);
const text = ref('');
const emojis = ref(['👍', '👎', '😆', '😍', '🔥']);
const yTranslate = ref(false);
const xTranslate = ref(false);
const marginTop = ref(0);
const shake = ref(false);

const { allowCommenting, draft } = storeToRefs(draftStore);
const {
  points,
  deletedComments,
  canCloseTempPoint,
  activePoint,
  commentMenuTop,
  commentMenuId,
  commentMenuUserId,
  showColorPicker,
  text: storeText,
} = storeToRefs(draftImageStore);

const tempDeletePointId = ref(null);
const tempDeleteCommentId = ref(null);

function isDeletedComments(id: number) {
  return [...deletedComments.value].indexOf(id) !== -1;
}

const resize = () => {
  if (textareaRef.value) {
    textareaRef.value.style.height = 25 + 'px';
    textareaRef.value.style.height = textareaRef.value.scrollHeight + 'px';
  }
};

const calculateStyle = () => {
  const drawingAreaEl = document.querySelector('.drawing-area') as HTMLElement | null;
  if (!drawingAreaEl || !messengerRef.value) return;
  const drawingArea = drawingAreaEl.getBoundingClientRect();
  const bounds = messengerRef.value.getBoundingClientRect();
  // const draftZoomContainer = document.querySelector("#draft-zoom-container")?.getBoundingClientRect();
  // const windowSize = !!draftZoomContainer ? {
  //   height: draftZoomContainer.height,
  //   width: draftZoomContainer.width
  // } : {height: window.innerHeight, width: window.innerWidth};
  const x = (props.point.left * drawingArea.width) / 100 + drawingArea.x - bounds.width - 11 - 8;
  const y = (props.point.top * drawingArea.height) / 100 + drawingArea.y + 13 + 8;

  xTranslate.value = x < 14 + 8;
  // yTranslate.value = y + bounds.height > windowSize.height - 14;

  // if (y + bounds.height > windowSize.height - 14 - 8) {
  //   marginTop.value = (windowSize.height - 14 - 8 - y - bounds.height)
  // } else {
  //   marginTop.value = 0
  // }
};

async function send() {
  const textValue = text.value;

  if (props.point.id) {
    if (sendingComment.value) {
      return;
    }
    text.value = '';
    sendingComment.value = true;
    await draftImageStore.createComment({ text: textValue, pointId: props.point.id });
    sendingComment.value = false;
    setTimeout(() => {
      resize();
      textareaRef.value?.focus();
    }, 0);
  } else {
    text.value = '';
    setTimeout(resize, 0);
    await draftImageStore.createPoint(textValue);
  }
}

async function deletePoint() {
  const id = props.point.id;
  if (!canCloseTempPoint.value) {
    draftImageStore.setCanCloseTempPoint(true);
  }

  if (id) {
    draftImageStore.deletePoint(id);
    tempDeletePointId.value = id;

    showNotification('delete_point', {
      data: id,
      emoji: '❌',
      title: isSmartphone.value ? 'Point deleted' : 'The point has been permanently deleted',
      ok_action: isSmartphone.value
        ? undefined
        : () => {
            tempDeletePointId.value = null;
          },
      undo_action: () => {
        const tempId = tempDeletePointId.value;
        if (tempId) {
          draftImageStore.undoDeletePoint(tempId);
        }
      },
    });
  } else {
    draftImageStore.closeTempPoint();
  }
}

function openColorPicker() {
  draftImageStore.toggleColorPicker();
}

async function deleteComment() {
  const id = commentMenuId.value;
  if (id) {
    tempDeleteCommentId.value = id;
    draftImageStore.deleteComment(id);

    showNotification('delete_comment', {
      data: id,
      emoji: '❌',
      title: isSmartphone.value ? 'Comment deleted' : 'The comment has been permanently deleted',
      ok_action: isSmartphone.value
        ? undefined
        : () => {
            tempDeleteCommentId.value = null;
          },
      undo_action: () => {
        const tempId = tempDeleteCommentId.value;
        if (tempId) {
          draftImageStore.undoDeleteComment(tempId);
        }
      },
    });
  }
}

function onCloseTempPoint(event) {
  shake.value = true;
  textareaRef.value?.focus();
  setTimeout(() => {
    textareaRef.value?.focus();
  }, 0);
  setTimeout(() => (shake.value = false), 2000);
}

function copyComment() {
  draftImageStore.copyComment();
}

async function approvePoint() {
  await draftImageStore.approvePoint();
}

function scrollToComment(event) {
  const id = event.detail;
  if (id) {
    document.getElementById(`comment-${id}`).scrollIntoView();
  }
}

function addContentAtCursorFunc(elem, newContent) {
  const length = newContent.length;
  if (elem.selectionStart || elem.selectionStart == '0') {
    let _startSelection = elem.selectionStart;
    let _endSelection = elem.selectionEnd;
    text.value = elem.value.substring(0, _startSelection) + newContent + elem.value.substring(_endSelection, elem.value.length);
    elem.setSelectionRange(_startSelection + length, _startSelection + length);
  } else {
    text.value += newContent;
  }
}

function emojiClick(event, emoji) {
  event.preventDefault();
  if (textareaRef.value) {
    textareaRef.value?.focus();
  }
  addContentAtCursorFunc(textareaRef.value, emoji);
}

function onSetMessengerText(event) {
  text.value = event.detail;
}

function textareaKeydown(event) {
  const keyCode = event.which || event.keyCode;

  if (keyCode === 13 && !event.shiftKey) {
    event.preventDefault();
    send();
  }
}

function blur(event) {
  // if (textareaRef.value) {
  //   textareaRef.value.focus()
  // }
}

function close() {
  draftImageStore.closeTempPoint();
}

// Point comments
const comments = computed(() => {
  const point = points.value.find((item: any) => item.id === props.point.id);
  if (!point || !point.comments) {
    return [];
  }
  return point.comments
    .filter((item: any) => {
      if (!item || !item.comment || !item.comment.id) {
        return false;
      }
      return !isDeletedComments(item.comment.id);
    })
    .map(({ comment, read, sender }: any) => ({ ...comment, read, sender }));
});
// Need show loader
const loading = computed(() => props.loading);
// Current system user
const user = computed(() => (window as any).g_opt.user);
// Show delete point button
const showDeleteDraft = computed(() => {
  return draft.value?.owner_id === user.value.id || props.point.owner_id === user.value.id || !props.point.owner_id;
});
// Show Done button
const showDoneButton = computed(() => {
  return props.point.owner_id === user.value.id;
});

const showDeleteComment = computed(() => {
  return (
    (commentMenuUserId.value === user.value.id && comments.value.length > 1) || (draft.value?.owner_id === user.value.id && comments.value.length > 1)
  );
});

const pointLocation = computed(() => {
  return {
    top: props.point.top,
    left: props.point.left,
  };
});

const sendButtonDisabled = computed(() => !text.value.trim() || loading.value || text.value.length > 1000);

onClickOutside(colorPickerButton, (event) => draftImageStore.toggleColorPicker(false));

watch(text, (newVal) => {
  resize();

  draftImageStore.setText(newVal);

  if (newVal.length > 1000) {
    showNotification('copy_draft', {
      emoji: '🙁',
      title: isSmartphone.value ? 'Limit is 1000 char.' : 'The maximum number of characters in a comment is 1000',
      ok_action: () => {},
    });
  }

  if (newVal.length > 4) {
    draftImageStore.setCanCloseTempPoint(false);
  } else {
    draftImageStore.setCanCloseTempPoint(true);
  }
});

watch([comments], () => {
  if (!isSmartphone.value) {
    textareaRef.value?.focus();
  }
});

watch([text, comments], () => {
  setTimeout(calculateStyle, 0);
});

watch([comments, commentsRef], () => {
  setTimeout(() => {
    if (commentsRef.value) {
      commentsRef.value.scrollTop = commentsRef.value.scrollHeight;
    }
  }, 0);
});

watch(pointLocation, (newVal, oldVal) => {
  if (newVal.left !== oldVal.left || newVal.top !== oldVal.top) {
    opacity.value = 0;
    setTimeout(() => {
      calculateStyle();
      opacity.value = 1;
    }, 0);
  } else {
    calculateStyle();
  }
});

onMounted(() => {
  calculateStyle();
  if (!isSmartphone.value) {
    textareaRef.value?.focus();
  }

  draftImageStore.setText(text.value);

  window.addEventListener('scrollToComment', scrollToComment);
  window.addEventListener('closeTempPoint', onCloseTempPoint);
  window.addEventListener('setMessengerText', onSetMessengerText);
});

onUnmounted(() => {
  window.removeEventListener('scrollToComment', scrollToComment);
  window.removeEventListener('closeTempPoint', onCloseTempPoint);
  window.removeEventListener('setMessengerText', onSetMessengerText);
});

const styles = computed(() => {
  if (isSmartphone.value) {
    return {
      bottom: '16px',
      position: 'fixed' as const,
      top: 'auto',
      transform: 'none',
      left: '16px',
      right: '16px',
      width: 'auto',
      maxHeight: 'calc(100% - 32px)',
    };
  }

  return {
    left: pointLocation.value.left + '%',
    top: pointLocation.value.top + '%',
    opacity: opacity.value,
    marginTop: marginTop.value * props.zoom + 'px',
  };
});

const classes = computed(() => {
  if (isSmartphone.value) {
    return {
      'shake-mobile': shake.value,
    };
  }

  return {
    'translate-x': xTranslate.value,
    'translate-y': yTranslate.value,
    shake: shake.value,
  };
});
</script>

<template>
  <div ref="messengerRef" class="messenger shadow-menu-s bg-white dark:bg-gray-900" :style="styles" :class="classes">
    <div v-show="loading" class="messenger__loader bottom-[48px] rounded-t-8 bg-white dark:bg-gray-900 max-tablet:bottom-[60px]">
      <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 22 22" fill="none" class="rotate">
        <path
          class="stroke-black dark:stroke-gray-500"
          d="M20.5832 11C20.5832 16.2927 16.2926 20.5834 10.9998 20.5834C5.70711 20.5834 1.4165 16.2927 1.4165 11C1.4165 5.70729 5.70711 1.41669 10.9998 1.41669"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    </div>

    <div class="max-tablet:pb-[5px]">
      <div
        v-if="showDoneButton || showDeleteDraft"
        class="messenger__header px-[12px] pb-[4px] pt-[12px] max-tablet:pl-s max-tablet:pr-xs max-tablet:pt-xs"
      >
        <span v-if="!showDoneButton"></span>
        <button
          v-if="showDoneButton"
          class="tppr-button__animation flex h-[24px] w-[24px] items-center justify-center rounded-4 stroke-gray-900 hover:bg-gray-250 active:bg-gray-150 dark:stroke-gray-500 dark:hover:bg-gray-700 dark:hover:stroke-white dark:active:bg-gray-800"
          @click="approvePoint"
        >
          <svg v-if="!activePoint.approve" xmlns="http://www.w3.org/2000/svg" width="18" height="13" viewBox="0 0 18 13" fill="none" class="mb-[1px]">
            <path d="M1 8.00002L5.5 12.5L17 1.00002" stroke-linecap="round" stroke-linejoin="round" />
          </svg>

          <svg v-if="activePoint.approve" xmlns="http://www.w3.org/2000/svg" width="18" height="14" viewBox="0 0 18 14" fill="none">
            <path d="M1 8.00002L5.5 12.5L17 1.00002" stroke="#00B027" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </button>

        <button
          v-if="showDeleteDraft"
          ref="colorPickerButton"
          class="flex h-[24px] w-[24px] items-center justify-center rounded-4 max-tablet:h-[40px] max-tablet:w-[40px] max-tablet:rounded-8 max-tablet:active:bg-gray-250 dark:max-tablet:active:bg-gray-800"
          @click="openColorPicker"
        >
          <i v-if="activePoint.color === 10" class="application-icon-inversion flex h-[18px] w-[18px] max-tablet:h-m max-tablet:w-m"></i>

          <span
            v-if="activePoint.color !== 10"
            class="flex h-[18px] w-[18px] rounded-2 max-tablet:h-m max-tablet:w-m max-tablet:rounded-4"
            :style="{ 'background-color': getColorHEX(activePoint.color) }"
          ></span>
        </button>

        <ColorPicker v-if="showColorPicker"></ColorPicker>
      </div>
    </div>

    <div v-if="comments.length > 0" ref="commentsRef" class="messenger__comments px-s py-[12px] max-tablet:pb-[4px]">
      <comment v-for="comment in comments" :key="comment.id" :can-highlight="true" :comment="comment"></comment>
    </div>

    <div v-if="allowCommenting" class="messenger__text-field">
      <textarea
        ref="textareaRef"
        v-model="text"
        rows="1"
        placeholder="Write a comment here"
        class="min-h-[34px] bg-white text-[15px] text-gray-900 placeholder:text-gray-400 focus:bg-gray-200 dark:bg-gray-900 dark:text-white dark:placeholder:text-gray-600 dark:focus:bg-gray-700 max-tablet:min-h-[44px] max-tablet:!px-[9px] max-tablet:!pb-[9px] max-tablet:!pt-[10px] max-tablet:text-[17px]"
        @keydown="textareaKeydown"
        @blur="blur"
      />
    </div>

    <div v-if="allowCommenting" class="messenger__divider bg-gray-200 dark:bg-gray-800"></div>

    <div v-if="allowCommenting" class="messenger__footer">
      <div class="flex justify-between">
        <div v-if="!isSmartphone" class="flex items-center pl-[4px]">
          <button
            v-for="emoji in emojis"
            :key="emoji"
            class="tppr-button__animation flex h-[25px] w-[25px] items-center justify-center rounded-4 text-[17px] hover:bg-gray-200 active:bg-gray-100 dark:hover:bg-gray-700 dark:active:bg-gray-800"
            @mousedown="emojiClick($event, emoji)"
          >
            {{ emoji }}
          </button>
        </div>

        <div v-if="isSmartphone">
          <button
            v-if="showDeleteDraft && !loading"
            class="tppr-button__animation mr-[7px] flex h-[44px] w-[44px] items-center justify-center rounded-8 bg-gray-250 stroke-gray-600 active:bg-gray-100 dark:bg-gray-700 dark:stroke-gray-500 dark:active:bg-gray-800"
            @click="deletePoint()"
          >
            <svg width="44" height="44" viewBox="0 0 44 44" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="17" y="19" width="10" height="11" rx="1" stroke-width="1.3" />
              <rect x="16" y="16" width="12" height="3" rx="1" stroke-width="1.3" />
              <rect x="20" y="13" width="4" height="3" rx="1" stroke-width="1.3" />
              <path d="M20.5 22L20.5 27" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round" />
              <path d="M23.5 22L23.5 27" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
          </button>
        </div>

        <div class="flex items-center gap-xs max-tablet:gap-[4px]">
          <div v-if="!isSmartphone">
            <button
              v-if="showDeleteDraft && !loading && !isSmartphone"
              class="tppr-button__animation flex h-[25px] w-[25px] items-center justify-center rounded-4 stroke-[#6D6D6D] hover:bg-gray-200 active:bg-gray-100 dark:hover:bg-gray-700 dark:hover:stroke-white dark:active:bg-gray-800"
              @click="deletePoint()"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="25" height="25" viewBox="0 0 25 25" fill="none">
                <rect x="8" y="10" width="10" height="11" rx="1" stroke-width="1.3" />
                <rect x="7" y="7" width="12" height="3" rx="1" stroke-width="1.3" />
                <rect x="11" y="4" width="4" height="3" rx="1" stroke-width="1.3" />
                <path d="M11.5 13L11.5 18" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round" />
                <path d="M14.5 13L14.5 18" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
            </button>
          </div>
          <div v-if="!isSmartphone" class="flex w-[76px]">
            <button
              class="messenger__footer__send tppr-button__animation tppr-button s colored disabled:bg-gray-300 dark:disabled:bg-gray-700 dark:disabled:text-gray-400"
              :disabled="sendButtonDisabled"
              @click="send()"
            >
              <span v-if="!loading && !sendingComment" class="tppr-button__text !text-[13px]">Send</span>
              <svg v-else xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 22 22" fill="none" class="rotate">
                <path
                  class="stroke-white"
                  d="M20.5832 11C20.5832 16.2927 16.2926 20.5834 10.9998 20.5834C5.70711 20.5834 1.4165 16.2927 1.4165 11C1.4165 5.70729 5.70711 1.41669 10.9998 1.41669"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </button>
          </div>

          <div v-if="isSmartphone" class="flex w-[104px]">
            <button
              class="messenger__footer__send tppr-button__animation tppr-button h-[44px] w-[44px] !bg-black max-tablet:!rounded-8"
              @click="close()"
            >
              <span class="tppr-button__text">Close</span>
            </button>
          </div>
          <div v-if="isSmartphone" class="flex w-[104px]">
            <button
              class="messenger__footer__send tppr-button__animation tppr-button colored h-[44px] w-[44px] disabled:bg-gray-300 dark:disabled:bg-gray-700 dark:disabled:text-gray-400 max-tablet:!rounded-8"
              :disabled="sendButtonDisabled"
              @click="send()"
            >
              <span v-if="!loading && !sendingComment" class="tppr-button__text">Send</span>
              <svg v-else xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 22 22" fill="none" class="rotate">
                <path
                  class="stroke-white"
                  d="M20.5832 11C20.5832 16.2927 16.2926 20.5834 10.9998 20.5834C5.70711 20.5834 1.4165 16.2927 1.4165 11C1.4165 5.70729 5.70711 1.41669 10.9998 1.41669"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="!!commentMenuTop"
      class="messenger__comment-menu shadow-menu-s absolute right-[-200px] flex w-[205px] flex-col gap-[2px] overflow-hidden rounded-8 bg-white p-[6px] dark:bg-gray-900 max-tablet:right-[48px]"
      :style="{ top: commentMenuTop }"
    >
      <button
        class="flex h-[36px] items-center rounded-4 pl-[12px] pr-[12px] hover:bg-gray-200 active:bg-gray-100 dark:hover:bg-gray-700 dark:active:bg-gray-800"
        @click="copyComment"
      >
        <span class="mr-[10px] flex h-[16px] w-[16px] items-center justify-center">
          <svg class="stroke-black dark:stroke-white" xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path
              d="M6.04688 7.45312V7.45312C7.02319 8.42943 8.6061 8.42943 9.58242 7.45312L11.6711 5.36445C12.6474 4.38814 12.6474 2.80523 11.6711 1.82892V1.82892C10.6948 0.852606 9.11186 0.852606 8.13555 1.82892L7.00012 2.96435"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              d="M7.67089 5.82892V5.82892C6.69458 4.85261 5.11167 4.85261 4.13536 5.82892L2.04669 7.91759C1.07038 8.8939 1.07038 10.4768 2.04669 11.4531V11.4531C3.023 12.4294 4.60591 12.4294 5.58222 11.4531L6.71765 10.3177"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </span>
        <span class="text-caption-s text-black dark:text-white">Copy comment link</span>
      </button>
      <div v-if="showDeleteComment" class="tppr-divider mx-[8px]"></div>
      <button
        v-if="showDeleteComment"
        class="flex h-[36px] items-center rounded-4 pl-[12px] pr-[12px] hover:bg-gray-200 active:bg-gray-100 dark:hover:bg-gray-700 dark:active:bg-gray-800"
        @click="deleteComment"
      >
        <span class="mr-[10px] flex h-[16px] w-[16px] items-center justify-center">
          <svg class="stroke-black dark:stroke-white" xmlns="http://www.w3.org/2000/svg" width="12" height="14" viewBox="0 0 12 13" fill="none">
            <rect x="2" y="4.49996" width="8" height="8" rx="1" />
            <rect x="1" y="2.49996" width="10" height="2" rx="1" />
            <rect x="4" y="0.499958" width="4" height="2" rx="1" />
            <path d="M4 6.49995L4 10.5" stroke-linecap="round" stroke-linejoin="round" />
            <path d="M6 6.49995L6 10.5" stroke-linecap="round" stroke-linejoin="round" />
            <path d="M8 6.49995L8 10.5" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </span>
        <span class="text-caption-s text-black dark:text-white">Delete comment</span>
      </button>
    </div>
  </div>
</template>

<style scoped lang="scss">
$inter-font: 'Inter', sans-serif;

@mixin move-shaking($postfix, $translate) {
  @keyframes move-shaking-animation-#{$postfix} {
    from {
    }

    10%,
    20% {
      transform: $translate rotate3d(0, 0, 1, -3deg);
    }

    30%,
    50%,
    70%,
    90% {
      transform: $translate rotate3d(0, 0, 1, 3deg);
    }

    40%,
    60%,
    80% {
      transform: $translate rotate3d(0, 0, 1, -3deg);
    }

    to {
    }
  }
}

@mixin move-shaking-animation() {
  @include move-shaking('default', translate(calc((100% + 19px) * -1), 21px));
}

@mixin move-shaking-animation-mobile() {
  @include move-shaking('default-mobile', translate(0, 0));
}

@mixin move-shaking-animation-x() {
  @include move-shaking('x', translate(19px, 21px));
}

@mixin move-shaking-animation-y() {
  @include move-shaking('y', translate(calc((100% + 19px) * -1), calc((100% + 21px) * -1)));
}

@mixin move-shaking-animation-xy() {
  @include move-shaking('xy', translate(19px, calc((100% + 21px) * -1)));
}

.messenger {
  mix-blend-mode: normal;
  position: absolute;
  border-radius: 8px;
  width: 275px;
  z-index: 1002;
  left: 294px;
  top: 14px;
  font-family: $inter-font;
  max-height: 830px;
  display: flex;
  flex-direction: column;
  transform: translate(calc((100% + 19px) * -1), 21px);

  &.shake-mobile {
    @include move-shaking-animation-mobile();
    animation: move-shaking-animation-default-mobile;
    animation-duration: 1.2s;
    animation-iteration-count: 1;
  }

  &.shake {
    @include move-shaking-animation();
    animation: move-shaking-animation-default;
    animation-duration: 1.2s;
    animation-iteration-count: 1;
  }

  &.translate-x {
    transform: translate(19px, 21px);

    &.shake {
      @include move-shaking-animation-x();
      animation: move-shaking-animation-x;
      animation-duration: 1.2s;
      animation-iteration-count: 1;
    }
  }

  &.translate-y {
    transform: translate(calc((100% + 19px) * -1), calc((100% + 21px) * -1));

    &.shake {
      @include move-shaking-animation-y();
      animation: move-shaking-animation-y;
      animation-duration: 1.2s;
      animation-iteration-count: 1;
    }
  }

  &.translate-x.translate-y {
    transform: translate(19px, calc((100% + 21px) * -1));

    &.shake {
      @include move-shaking-animation-xy();
      animation: move-shaking-animation-xy;
      animation-duration: 1.2s;
      animation-iteration-count: 1;
    }
  }

  &__loader {
    display: flex;
    justify-content: center;
    align-items: center;
    position: absolute;
    top: 0;
    //bottom: 48px;
    width: 100%;
    z-index: 1;
  }

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    //padding: 12px 12px 0;
  }

  &__comments {
    //padding: 11px 16px 12px;
    overflow: auto;
    overflow-x: hidden;
  }

  &__text-field {
    padding: 8px;

    textarea {
      resize: none;
      width: 100%;
      font-style: normal;
      font-weight: 500;
      line-height: 24.6px;
      padding: 5px 8px 4px;
      outline: none;
      max-height: 225px;
      border-radius: 4px;
    }
  }

  &__divider {
    height: 1px;
    margin: 0 8px;
  }

  &__footer {
    padding: 8px;

    &__send {
      font-size: 13px;
      font-style: normal;
      font-weight: 400;
      line-height: normal;
      letter-spacing: -0.39px;
      border-radius: 6px;
    }
  }

  &__comment-menu {
    .tppr-divider {
      background: repeating-linear-gradient(90deg, #cfcfcf 0 2px, transparent 0 4px);
    }
  }
}

.dark .messenger {
  &__comment-menu {
    .tppr-divider {
      background: repeating-linear-gradient(90deg, #515151 0 2px, transparent 0 4px);
    }
  }
}
</style>
